import { isLocationKey, isSensitiveLocation } from "./sensitive-location";

/**
 * Shared PostHog `before_send` primitives for Sill's credential routes
 * (`/invite/<token>`, `/marketplace/claims/<token>`, `/share/<token>`, their
 * `/api/public/...` twins — see `isSensitiveLocation`). Consumer and Marketing
 * each own their app's allow/drop policy; both build it from these so neither
 * can drop PostHog's ingestion keys or forward a remembered credential URL.
 *
 * Typed structurally so `@sill/shared` needs no posthog-js dependency;
 * posthog-js's `CaptureResult` satisfies {@link PostHogCaptureLike}.
 */

type Bag = Record<string, unknown>;

export interface PostHogCaptureLike {
  properties: Bag;
  $set?: Bag;
  $set_once?: Bag;
}

/**
 * PostHog's own ingestion + identity keys, which a rebuilt event MUST keep
 * beside its bounded bag. posthog-js runs `before_send` AFTER stamping these,
 * and DROPS any event whose `token` a hook removed (`_runBeforeSend` +
 * `@posthog/core` `knownUnsafeEditableEventProperty`: "required for ingestion,
 * so the event will be dropped"). `token` is always the project api key —
 * `calculateEventProperties` writes `config.token` over any app-supplied value
 * — never a Sill credential. Losing `distinct_id`/`$device_id` ingests the
 * event as a fresh anonymous person; the session/window ids tie it to replay.
 * None of these can carry a URL, path, or referral token.
 *
 * Verified against posthog-js 1.428.1 (lockfile) + `@posthog/core` 1.51.0.
 */
export const POSTHOG_INGESTION_KEYS = [
  "token",
  "distinct_id",
  "$device_id",
  "$session_id",
  "$window_id",
  "$lib",
  "$lib_version",
  "$insert_id",
  "$time",
  "$process_person_profile",
  "$is_identified",
  "$cookieless_mode",
] as const;

/**
 * Where the SESSION or PERSON began, not where this event fired. posthog-js
 * (`SessionPropsManager.getSessionProps`) stamps `$session_entry_url`,
 * `$session_entry_pathname`, `$session_entry_referrer`, ... onto EVERY event of
 * the session, and `$set_once` carries `$initial_current_url` & co. A session
 * that began on a credential route would otherwise leak the token on every
 * later pageview/autocapture.
 */
const SESSION_ORIGIN_KEY = /^\$(session_entry|initial)_/;

/** Property names that can carry a URL, path, host, or referrer. */
const LOCATION_SHAPED_KEY = /url|path|referr|href|location|host/i;

export function isSessionOriginKey(key: string): boolean {
  return SESSION_ORIGIN_KEY.test(key);
}

/** Names a URL/path/host/referrer by itself (`$initial_utm_source` does not). */
function isLocationShapedKey(key: string): boolean {
  return isLocationKey(key) || LOCATION_SHAPED_KEY.test(key);
}

/** Pass-through scrub: a location-shaped or session-origin key whose value is
 * a credential route. */
function carriesRememberedCredential(key: string, value: unknown): boolean {
  return (
    (isSessionOriginKey(key) || isLocationShapedKey(key)) &&
    carriesSensitiveLocation(value)
  );
}

/** Rebuild scrub: every location-shaped key, plus any credential value. */
function revealsLocation(key: string, value: unknown): boolean {
  return isLocationShapedKey(key) || carriesRememberedCredential(key, value);
}

/** Any string at or under `value` is a credential route. Only ever applied
 * under a location-shaped or session-origin key, so free text elsewhere (DOM
 * text, CTA ids) is never token-scanned. */
function carriesSensitiveLocation(
  value: unknown,
  seen = new WeakSet<object>(),
): boolean {
  if (typeof value === "string") return isSensitiveLocation(value);
  if (!value || typeof value !== "object" || seen.has(value)) return false;
  seen.add(value);
  return Object.values(value).some((nested) =>
    carriesSensitiveLocation(nested, seen),
  );
}

type RemoveEntry = (key: string, value: unknown) => boolean;

/** `bag` minus every entry `remove` selects; `bag` itself when nothing was
 * removed, `undefined` when the removal emptied it. */
function withoutEntries(bag: Bag, remove: RemoveEntry): Bag | undefined {
  const kept: Bag = {};
  let removed = false;
  for (const [key, value] of Object.entries(bag)) {
    if (remove(key, value)) {
      removed = true;
      continue;
    }
    kept[key] = value;
  }
  if (!removed) return bag;
  return Object.keys(kept).length > 0 ? kept : undefined;
}

function withPersonProperties<T extends PostHogCaptureLike>(
  event: T,
  remove: RemoveEntry,
): T {
  let next = event;
  for (const field of ["$set", "$set_once"] as const) {
    const bag = event[field];
    if (!bag || typeof bag !== "object") continue;
    const scrubbed = withoutEntries(bag, remove);
    if (scrubbed === bag) continue;
    if (next === event) next = { ...event };
    if (scrubbed === undefined) delete next[field];
    else next[field] = scrubbed;
  }
  return next;
}

/**
 * For an event `before_send` lets through: deletes any location-shaped
 * property — `$session_entry_*`, `$initial_*`, `$referrer`, `*url*`, ... — in
 * `properties`, `$set`, or `$set_once` whose value is a credential route.
 * Scrubs keys instead of dropping the event, because session-origin keys ride
 * every event for the session's life. Returns `event` itself when nothing
 * matched; never mutates it.
 */
export function scrubSensitiveLocationProperties<T extends PostHogCaptureLike>(
  event: T,
): T {
  const properties = withoutEntries(
    event.properties,
    carriesRememberedCredential,
  );
  const next =
    properties === event.properties
      ? event
      : { ...event, properties: properties ?? {} };
  return withPersonProperties(next, carriesRememberedCredential);
}

/**
 * A sensitive event rebuilt to exactly `bag` + PostHog's ingestion/identity
 * keys (primitive values only). Every location-shaped `$set`/`$set_once` key
 * (`$initial_current_url`, `$initial_referrer`, ...) is dropped, credential or
 * not, so the rebuilt event says nothing about where it fired; non-location
 * initial props such as `$initial_utm_source` are kept, since posthog-js sends
 * them once and losing them would lose the person's attribution.
 */
export function rebuildBoundedEvent<T extends PostHogCaptureLike>(
  event: T,
  bag: Bag,
): T {
  const properties: Bag = { ...bag };
  for (const key of POSTHOG_INGESTION_KEYS) {
    const value = event.properties[key];
    if (
      typeof value === "string" ||
      typeof value === "number" ||
      typeof value === "boolean"
    ) {
      properties[key] = value;
    }
  }
  return {
    ...withPersonProperties(event, revealsLocation),
    properties,
  };
}
